import React, { useState, useEffect } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { candidateService } from '../services/candidateService';
import { useNavigate } from 'react-router-dom';

const CandidateDetailsModal = ({ candidateId, onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
  const [candidateData, setCandidateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentPreview, setDocumentPreview] = useState({
    isOpen: false,
    title: '',
    rawUrl: '',
    previewUrl: '',
    previewType: 'pdf'
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCandidateDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  const fetchCandidateDetails = async () => {
    try {
      setLoading(true);
      const response = await candidateService.getAdminCandidateDetails(candidateId);
      setCandidateData(response.data);
    } catch (err) {
      setError('Failed to load candidate details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewApplication = (application) => {
    const applicationId = application._id || application.id;
    navigate(`/applications/${applicationId}`);
    onClose();
  };

  const buildDocumentPreview = (url, filename) => {
    const lowerName = String(filename || '').toLowerCase();
    const lowerUrl = String(url || '').toLowerCase();

    if (lowerName.endsWith('.pdf') || lowerUrl.includes('.pdf')) {
      return {
        previewType: 'pdf',
        previewUrl: `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
      };
    }

    if (lowerName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) || lowerUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/i)) {
      return {
        previewType: 'image',
        previewUrl: url
      };
    }

    return {
      previewType: 'unsupported',
      previewUrl: url
    };
  };

  const openDocumentPreview = (url, filename) => {
    if (!url) return;

    const preview = buildDocumentPreview(url, filename);

    setDocumentPreview({
      isOpen: true,
      title: filename || 'Document Preview',
      rawUrl: url,
      previewUrl: preview.previewUrl,
      previewType: preview.previewType
    });
  };

  const closeDocumentPreview = () => {
    setDocumentPreview({
      isOpen: false,
      title: '',
      rawUrl: '',
      previewUrl: '',
      previewType: 'pdf'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Registration': 'bg-blue-100 text-blue-800',
      'Agreement': 'bg-purple-100 text-purple-800',
      'Application Submitted': 'bg-indigo-100 text-indigo-800',
      'Under HR Review': 'bg-yellow-100 text-yellow-800',
      'Interview Scheduled': 'bg-orange-100 text-orange-800',
      'Offer Letter': 'bg-green-100 text-green-800',
      'Document Verification': 'bg-teal-100 text-teal-800',
      'Exam': 'bg-red-100 text-red-800',
      'Medical': 'bg-pink-100 text-pink-800',
      'Visa Process': 'bg-cyan-100 text-cyan-800',
      'Ticket': 'bg-lime-100 text-lime-800',
      'Departure': 'bg-emerald-100 text-emerald-800',
      'Completed': 'bg-green-200 text-green-900',
      'Rejected': 'bg-red-200 text-red-900'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatApplicationDisplayId = (application) => {
    const rawId = application?._id || application?.id;
    if (!rawId) return 'N/A';

    const idStr = rawId.toString();
    const country = (application?.destinationCountry || '').trim();
    const countryPrefix = country
      ? country.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase().padEnd(2, 'X')
      : 'XX';

    const stableSuffix = idStr.slice(-6).toUpperCase();
    return `${countryPrefix}${stableSuffix}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-5" onClick={onClose}>
        {/* Previous Button */}
        {hasPrevious && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          >
            <MdChevronLeft size={28} />
          </button>
        )}

        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[75vh] overflow-y-auto shadow-[0_10px_40px_rgba(0,0,0,0.2)] mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 text-center text-text-secondary text-sm">Loading...</div>
        </div>

        {/* Next Button */}
        {hasNext && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          >
            <MdChevronRight size={28} />
          </button>
        )}
      </div>
    );
  }

  if (error || !candidateData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-5" onClick={onClose}>
        {/* Previous Button */}
        {hasPrevious && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          >
            <MdChevronLeft size={28} />
          </button>
        )}

        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[75vh] overflow-y-auto shadow-[0_10px_40px_rgba(0,0,0,0.2)] mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 text-center text-text-secondary text-sm">{error || 'Candidate not found'}</div>
          <button className="px-4 py-2 bg-[#F5F5F5] text-text-primary rounded-sm font-semibold text-sm cursor-pointer transition-all duration-200 hover:bg-[#E0E0E0]" onClick={onClose}>Close</button>
        </div>

        {/* Next Button */}
        {hasNext && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          >
            <MdChevronRight size={28} />
          </button>
        )}
      </div>
    );
  }

  const { candidate, applications } = candidateData;

  // Temporary debugging to verify photo URL resolution
  const photoUrl = candidate.profilePhoto?.url || 
                  candidate.personalInformation?.profilePhoto?.url || 
                  candidate.photoUrl;
  
  if (photoUrl) {
    console.log('✓ Profile photo found:', photoUrl);
  } else {
    console.log('✗ No profile photo found. Available fields:', {
      profilePhoto: candidate.profilePhoto,
      personalInformation: candidate.personalInformation,
      photoUrl: candidate.photoUrl
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-5" onClick={onClose}>
      {/* Previous Button */}
      {hasPrevious && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          title="Previous Candidate"
        >
          <MdChevronLeft size={28} />
        </button>
      )}

      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-[0_10px_40px_rgba(0,0,0,0.2)] mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-4 py-3 border-b border-[#E0E0E0]">
          <h2 className="m-0 text-base font-bold text-primary">Candidate Details</h2>
          <button className="bg-transparent border-none text-[24px] text-[#999] cursor-pointer p-0 w-7 h-7 flex items-center justify-center rounded transition-all duration-200 hover:bg-[#F5F5F5] hover:text-[#333]" onClick={onClose}>&times;</button>
        </div>

        <div className="p-4">
          {/* Profile Photo Section */}
          <div className="flex justify-center mb-6">
            {(() => {
              // Check multiple possible locations for profile photo
              const photoUrl = candidate.profilePhoto?.url || 
                              candidate.personalInformation?.profilePhoto?.url || 
                              candidate.photoUrl;
              
              return photoUrl ? (
                <img 
                  src={photoUrl} 
                  alt={`${candidate.firstName || candidate.name || ''} ${candidate.lastName || ''}`.trim() || 'Candidate'} 
                  className="w-24 h-24 rounded-full object-cover border-4 border-accent-green shadow-lg"
                  onError={(e) => {
                    console.error('Failed to load profile image:', photoUrl);
                    // Hide the broken image and show placeholder
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentNode.querySelector('.placeholder-avatar');
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
              ) : null;
            })()}
            {/* Placeholder avatar - shown when no photo or image fails to load */}
            <div className="placeholder-avatar w-24 h-24 rounded-full bg-gray-200 border-4 border-accent-green shadow-lg flex items-center justify-center" 
                 style={{display: (() => {
                   const photoUrl = candidate.profilePhoto?.url || 
                                   candidate.personalInformation?.profilePhoto?.url || 
                                   candidate.photoUrl;
                   return photoUrl ? 'none' : 'flex';
                 })()}}>
              <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div className="mb-4 last:mb-0">
            <h3 className="m-0 mb-2 text-[13px] font-semibold text-primary pb-1.5 border-b border-accent-green">Personal Information</h3>
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-1">
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Name:</span>
                <span className="text-[13px] text-text-primary">{candidate.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Email:</span>
                <span className="text-[13px] text-text-primary break-all">{candidate.email || 'Not provided'}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Phone:</span>
                <span className="text-[13px] text-text-primary">{candidate.mobile || candidate.phoneNumber || 'Not provided'}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Passport:</span>
                <span className="text-[13px] text-text-primary">{candidate.passportNumber}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Gender:</span>
                <span className="text-[13px] text-text-primary">{candidate.gender || 'Not provided'}</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">Location:</span>
                <span className="text-[13px] text-text-primary">
                  {candidate.location?.state && candidate.location?.district 
                    ? `${candidate.location.district}, ${candidate.location.state}`
                    : candidate.location?.state || 'Not provided'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Applications Section */}
          <div className="mb-4 last:mb-0">
            <h3 className="m-0 mb-2 text-[13px] font-semibold text-primary pb-1.5 border-b border-accent-green">Applied Applications ({applications.length})</h3>
            {applications.length === 0 ? (
              <div className="text-center py-4 text-text-secondary text-sm italic">No applications found</div>
            ) : (
              <div className="space-y-2">
                {applications.map((application) => (
                  <div key={application._id || application.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors duration-200">
                    <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Applied Date:</span>
                        <span className="text-[11px] text-text-primary">{formatDate(application.appliedAt)}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Application ID:</span>
                        <span className="text-[11px] text-text-primary font-mono">{formatApplicationDisplayId(application)}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Job Position:</span>
                        <span className="text-[11px] text-text-primary font-medium">{application.jobTitle}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Country:</span>
                        <span className="text-[11px] text-text-primary">{application.destinationCountry || 'Not specified'}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                          {application.status}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Current Stage:</span>
                        <span className="text-[11px] text-text-primary">{application.currentStage || application.status}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide">Payment Status:</span>
                        <span className="text-[11px] text-text-primary">{application.paymentStatus || 'Not specified'}</span>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleViewApplication(application)}
                        className="px-3 py-1 bg-accent-green text-white border-none rounded text-xs font-semibold cursor-pointer transition-all duration-200 hover:bg-[#5a6e4d]"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents Section */}
          <div className="mb-4 last:mb-0">
            <h3 className="m-0 mb-2 text-[13px] font-semibold text-primary pb-1.5 border-b border-accent-green">Documents</h3>
            <div className="flex flex-wrap gap-2">
              {/* Resume */}
              {candidate.documents?.resume?.url && (
                <button
                  type="button"
                  onClick={() => openDocumentPreview(candidate.documents.resume.url, 'Resume')}
                  className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
                >
                  Resume
                </button>
              )}

              {/* Combined Documents */}
              {candidate.documents?.combinedDocuments?.url && (
                <button
                  type="button"
                  onClick={() => openDocumentPreview(candidate.documents.combinedDocuments.url, 'Combined Documents')}
                  className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
                >
                  Combined Documents
                </button>
              )}

              {/* Additional Documents */}
              {candidate.additionalDocuments && candidate.additionalDocuments.map((doc, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => openDocumentPreview(doc.documentUrl || doc.url, doc.documentName || doc.name || `Document ${index + 1}`)}
                  className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
                >
                  {doc.documentName || doc.name || `Document ${index + 1}`}
                </button>
              ))}

              {/* No documents message */}
              {!candidate.documents?.resume?.url && 
               !candidate.documents?.combinedDocuments?.url && 
               (!candidate.additionalDocuments || candidate.additionalDocuments.length === 0) && (
                <div className="text-center py-2 text-text-secondary text-xs italic w-full">No documents uploaded</div>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 py-2.5 border-t border-[#E0E0E0] flex justify-end gap-2">
          <button className="px-3 py-1.5 bg-[#F5F5F5] text-text-primary border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#E0E0E0]" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Next Button */}
      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white text-primary rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-[1001]"
          title="Next Candidate"
        >
          <MdChevronRight size={28} />
        </button>
      )}

      {/* Document Preview Modal */}
      {documentPreview.isOpen && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.6)] flex items-center justify-center z-[1100] p-4"
          onClick={closeDocumentPreview}
        >
          <div
            className="bg-white rounded-lg w-full max-w-5xl h-[88vh] flex flex-col shadow-[0_10px_40px_rgba(0,0,0,0.3)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="m-0 text-sm font-semibold text-primary truncate pr-4">{documentPreview.title}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={documentPreview.rawUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-1.5 px-3 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 no-underline"
                >
                  Open in New Tab
                </a>
                <button
                  type="button"
                  onClick={closeDocumentPreview}
                  className="py-1.5 px-3 rounded-md text-xs font-semibold bg-accent-green text-white hover:bg-[#5a6b4d]"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="h-[calc(88vh-56px)] bg-gray-50">
              {documentPreview.previewType === 'image' && (
                <div className="w-full h-full overflow-auto flex items-center justify-center bg-white">
                  <img src={documentPreview.previewUrl} alt={documentPreview.title} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              {(documentPreview.previewType === 'pdf' || documentPreview.previewType === 'office') && (
                <iframe
                  src={documentPreview.previewUrl}
                  title={documentPreview.title}
                  className="w-full h-full border-0"
                />
              )}
              {documentPreview.previewType === 'unsupported' && (
                <div className="w-full h-full flex items-center justify-center text-center px-6">
                  <div>
                    <p className="text-sm text-text-secondary mb-3">Preview is not available for this file type.</p>
                    <a
                      href={documentPreview.rawUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-2 px-4 rounded-md text-sm font-semibold bg-accent-green text-white hover:bg-[#5a6b4d] no-underline inline-block"
                    >
                      Download File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDetailsModal;
